import Column from "@/components/Ui/Column";
import Row from "@/components/Ui/Row";
import ClickToCopy from "@/components/portal/ClickToCopy";
import Title from "@/components/portal/sidebar/Title";
import { Button } from "@/components/shadcn/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/shadcn/popover";
import { Separator } from "@/components/shadcn/separator";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/shadcn/tooltip";
import { Code } from "lucide-react";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { ReactNode, useState } from "react";
import { twMerge } from "tailwind-merge";

type Props = {
  data?: unknown;
  variant?: "default" | "ghost";
  children?: ReactNode;
};

const getValueType = (val: unknown): number => {
  if (val === null || val === undefined || typeof val !== "object") return 0;
  if (Array.isArray(val)) return 1;
  return 2;
};

const sortEntries = (entries: [string, unknown][]) =>
  entries.sort(([aKey, aVal], [bKey, bVal]) => {
    const aType = getValueType(aVal);
    const bType = getValueType(bVal);
    if (aType !== bType) return aType - bType;
    return aKey.localeCompare(bKey);
  });

const sortData = (value: unknown): unknown => {
  if (value === null || value === undefined) return value;
  if (typeof value !== "object") return value;
  if ((value as { $$typeof?: unknown }).$$typeof) return value;

  if (Array.isArray(value)) {
    return value.map((item) => sortData(item));
  }

  const sorted: Record<string, unknown> = {};
  sortEntries(Object.entries(value)).forEach(([key, val]) => {
    sorted[key] = sortData(val);
  });
  return sorted;
};

const renderValue = (
  value: unknown,
  indent: number,
  seen: WeakSet<object>,
  isRoot = false,
): string => {
  if (value === null) return "null";
  if (value === undefined) return "undefined";
  if (typeof value === "function") return "[Function]";

  if (typeof value !== "object") {
    return String(value);
  }

  if ((value as { $$typeof?: unknown }).$$typeof) return "[JSX]";
  if (seen.has(value)) return "[Circular]";
  seen.add(value);

  if (Array.isArray(value)) {
    if (value.length === 0) return "";
    let result = "";
    value.forEach((item, index) => {
      const nextIndent = isRoot ? indent : indent + 1;
      const itemIndent = " ".repeat(nextIndent);

      if (typeof item === "object" && item !== null) {
        const itemCount = Array.isArray(item)
          ? `[${item.length}]`
          : `{${Object.keys(item).length}}`;
        const renderedItem = renderValue(item, nextIndent + 1, seen);
        result += `${itemIndent}#${index + 1}~${itemCount}\n`;
        if (renderedItem) result += `${renderedItem}\n`;
      } else {
        const renderedItem = renderValue(item, nextIndent, seen);
        result += `${itemIndent}#${index + 1}~${renderedItem}\n`;
      }
    });
    return result.trimEnd();
  }

  const entries = sortEntries(Object.entries(value));
  if (entries.length === 0) return "";

  let result = "";
  entries.forEach(([key, val]) => {
    const nextIndent = isRoot ? indent : indent + 1;
    const keyIndent = " ".repeat(nextIndent);

    if (typeof val === "object" && val !== null) {
      if ((val as { $$typeof?: unknown }).$$typeof) {
        result += `${keyIndent}${key}~[JSX]\n`;
      } else if (seen.has(val)) {
        result += `${keyIndent}${key}~[Circular]\n`;
      } else {
        const valCount = Array.isArray(val)
          ? `[${val.length}]`
          : `{${Object.keys(val).length}}`;
        const renderedVal = renderValue(val, nextIndent + 1, seen);
        result += `${keyIndent}${key}~${valCount}\n`;
        if (renderedVal) result += `${renderedVal}\n`;
      }
    } else {
      const renderedVal = renderValue(val, nextIndent, seen);
      result += `${keyIndent}${key}~${renderedVal}\n`;
    }
  });
  return result.trimEnd();
};

const DataInspector = ({ data, variant = "default", children }: Props) => {
  const hasData = data !== undefined;
  const [isOpen, setIsOpen] = useState(false);

  const dataInspector = useFeatureFlagEnabled("data-inspector");
  if (!dataInspector) return children;

  const button = (
    <Button
      disabled={!hasData}
      variant={children ? "secondary" : "outline"}
      size={children ? "sm" : "icon"}
      className={twMerge(
        children &&
          "hover:bg-input h-auto translate-x-1 -translate-y-1 p-1! opacity-80 hover:opacity-100",
      )}
    >
      <Code className={twMerge(children && "m-0! size-3 p-0!")} />
    </Button>
  );

  if (!hasData) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-block">{button}</span>
        </TooltipTrigger>
        <TooltipContent>No data to display</TooltipContent>
      </Tooltip>
    );
  }

  let formattedData: string;
  try {
    const seen = new WeakSet<object>();
    formattedData = renderValue(data, 0, seen, true).trim();
  } catch (error) {
    formattedData = `Error serializing data: ${error instanceof Error ? error.message : String(error)}`;
  }

  return (
    <>
      {isOpen && (
        <div className="animate-in fade-in-0 fixed inset-0 z-40 bg-black/50 backdrop-blur-sm duration-200" />
      )}
      <div
        className="relative grid"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        <div className="[grid-area:1/1]">{children}</div>
        <div className="row-up-right [grid-area:1/1]">
          <Popover modal={false} open={isOpen} onOpenChange={setIsOpen}>
            <PopoverTrigger asChild>{button}</PopoverTrigger>
            <PopoverContent
              align="start"
              className="custom-scrollbar custom-inline-scrollbar max-h-100 w-120 overflow-auto p-0"
            >
              <Column className="relative flex w-full gap-x-4 text-sm whitespace-pre-wrap">
                <Column className="bg-background/50 sticky top-0 w-full backdrop-blur-md">
                  <Row left className="w-full justify-between p-4">
                    <Title title="Showing Data"></Title>
                    <ClickToCopy
                      text={JSON.stringify(sortData(data), null, 2)}
                      label="Copy JSON"
                      className="h-8"
                    />
                  </Row>
                  <Separator />
                </Column>
                {formattedData.split("\n").map((line, i) => {
                  const match = line.match(/^(\s*)(.+?)~(.*)$/);
                  const bgClass = i % 2 === 1 ? "bg-border/15" : "";

                  if (match) {
                    const [, indent, key, value] = match;

                    return (
                      <Row
                        left
                        up
                        className={twMerge(
                          "w-full gap-2 px-2 py-1 font-mono",
                          bgClass,
                        )}
                        key={i}
                      >
                        <span className="text-muted-foreground inline-block shrink-0">
                          {indent}
                          {key}
                        </span>
                        <div className="min-w-0 flex-1 text-right break-all">
                          {value.match(/[\[\{].*?[\]\}]/) ? (
                            <span className="text-tertiary">{value}</span>
                          ) : (
                            value
                          )}
                        </div>
                      </Row>
                    );
                  }
                  return (
                    <div
                      key={i}
                      className={twMerge("w-full text-left", bgClass)}
                    >
                      {line}
                    </div>
                  );
                })}
              </Column>
            </PopoverContent>
          </Popover>
        </div>
      </div>
    </>
  );
};

export default DataInspector;
